/* eslint-disable react/prop-types */
"use client";

import React from "react";
import AppModalShell from "../common/AppModalShell";
import EventToolsBackButton from "../events/EventToolsBackButton";
import EventToolsSkeleton, {
  EventToolsAnimatedState,
} from "../events/EventToolsSkeletons";

function toNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function formatCompactNumber(value) {
  return new Intl.NumberFormat().format(toNumber(value));
}

function formatChartNumber(value) {
  const numeric = Math.max(0, toNumber(value));
  if (numeric >= 1000) {
    return new Intl.NumberFormat(undefined, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(numeric);
  }
  return new Intl.NumberFormat().format(numeric);
}

function normalizeRows(rows = []) {
  const normalized = Array.isArray(rows)
    ? rows
        .map((row) => ({
          ...row,
          event_day: String(row?.event_day || "").slice(0, 10),
        }))
        .filter((row) => row.event_day)
    : [];

  normalized.sort((a, b) => String(a.event_day).localeCompare(String(b.event_day)));
  return normalized;
}

function dayLabel(isoDate) {
  const raw = String(isoDate || "");
  if (!raw) return "";
  const [year, month, day] = raw.split("-");
  if (!year || !month || !day) return raw;
  return `${day}.${month}`;
}

function ChartBars({
  rows = [],
  totalKey = "",
  newKey = "",
  totalLabel = "",
  newLabel = "",
  title = "",
  theme = "event",
}) {
  const isClubTheme = theme === "club";
  const panelClass = isClubTheme
    ? "rounded-2xl border border-white/12 bg-white/[0.04] p-3"
    : "rounded-2xl border border-white/14 bg-black/20 p-3";
  const legendTextClass = isClubTheme ? "mt-2 flex items-center gap-4 text-[11px] text-white/74" : "mt-2 flex items-center gap-4 text-[11px] text-white/78";
  const totalLegendSwatchClass = isClubTheme ? "h-2.5 w-2.5 rounded-sm bg-white/45" : "h-2.5 w-2.5 rounded-sm bg-white/55";
  const totalBarClass = isClubTheme ? "absolute bottom-0 left-0 right-0 bg-white/38" : "absolute bottom-0 left-0 right-0 bg-white/45";
  const barFrameClass = isClubTheme
    ? "relative h-[136px] overflow-hidden rounded-md border border-white/16 bg-white/[0.03]"
    : "relative h-[136px] overflow-hidden rounded-md border border-white/18 bg-white/5";

  const data = React.useMemo(() => normalizeRows(rows), [rows]);
  const maxTotal = React.useMemo(() => {
    let max = 0;
    for (const row of data) {
      max = Math.max(max, toNumber(row?.[totalKey]));
    }
    return max > 0 ? max : 1;
  }, [data, totalKey]);

  if (!data.length) return null;
  const tickStep = Math.max(1, Math.ceil(data.length / 8));

  return (
    <div className={panelClass}>
      <div className="text-sm font-semibold text-white">{title}</div>
      <div className={legendTextClass}>
        <div className="flex items-center gap-1.5">
          <span className={totalLegendSwatchClass} />
          <span>{totalLabel}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-white" />
          <span>{newLabel}</span>
        </div>
      </div>
      <div className="mt-3 overflow-x-auto">
        <div className="flex min-w-max items-end gap-2 pb-1">
          {data.map((row, index) => {
            const totalValue = toNumber(row?.[totalKey]);
            const newValue = Math.min(totalValue, Math.max(0, toNumber(row?.[newKey])));
            const totalHeight = Math.max(4, Math.round((totalValue / maxTotal) * 132));
            const newHeight = Math.max(0, Math.round((newValue / maxTotal) * 132));
            const label = dayLabel(row.event_day);
            return (
              <div key={`${row.event_day}-${index}`} className="w-8 shrink-0">
                <div
                  className={barFrameClass}
                  title={`${row.event_day} · ${totalLabel}: ${formatCompactNumber(totalValue)} · ${newLabel}: ${formatCompactNumber(newValue)}`}
                >
                  <div
                    className={totalBarClass}
                    style={{ height: `${totalHeight}px` }}
                  />
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-white"
                    style={{ height: `${newHeight}px` }}
                  />
                </div>
                <div className="mt-1 space-y-0.5 text-center">
                  <div className="text-[9px] font-semibold leading-none text-white/90">
                    {formatChartNumber(totalValue)}
                  </div>
                  <div className="text-[9px] leading-none text-white/70">
                    {formatChartNumber(newValue)}
                  </div>
                </div>
                <div className="mt-1 h-4 text-center text-[10px] leading-none text-white/70">
                  {index % tickStep === 0 ? label : ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function HostAnalyticsModal({
  visible = false,
  onClose = () => {},
  title = "",
  theme = "event",
  loading = false,
  error = "",
  cards = [],
  rows = [],
  charts = [],
  closeLabel = "Close",
  backLabel = "Back",
  onBack = null,
  zIndexClassName = "z-[13000]",
  emptyLabel = "No analytics data yet.",
  embedded = false,
}) {
  const isClubTheme = theme === "club";
  const metricCardClass = isClubTheme
    ? "rounded-xl border border-white/14 bg-white/[0.04] px-3 py-2.5"
    : "rounded-xl border border-white/12 bg-white/[0.06] px-3 py-2.5";
  const emptyStateClass = isClubTheme
    ? "mt-4 rounded-xl border border-white/14 bg-white/[0.04] px-3 py-3 text-sm text-white/74"
    : "mt-4 rounded-xl border border-white/12 bg-white/[0.06] px-3 py-3 text-sm text-white/78";

  const normalizedRows = normalizeRows(rows);
  const content = error ? (
    <div className="rounded-xl border border-red-300/30 bg-red-500/20 px-3 py-2 text-sm text-red-50">
      {error}
    </div>
  ) : (
    <>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {cards.map((card) => (
          <div
            key={card.key || card.label}
            className={metricCardClass}
          >
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-white/72">
              {card.label}
            </div>
            <div className="mt-1 text-2xl font-extrabold leading-none">
              {formatCompactNumber(card.value)}
            </div>
          </div>
        ))}
      </div>

      {normalizedRows.length === 0 ? (
        <div className={emptyStateClass}>
          {emptyLabel}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {charts.map((chart) => (
            <ChartBars
              key={chart.id || chart.title}
              rows={normalizedRows}
              totalKey={chart.totalKey}
              newKey={chart.newKey}
              totalLabel={chart.totalLabel}
              newLabel={chart.newLabel}
              title={chart.title}
              theme={theme}
            />
          ))}
        </div>
      )}
    </>
  );

  if (!visible) return null;

  if (embedded) {
    return (
      <EventToolsAnimatedState
        loading={loading}
        skeleton={<EventToolsSkeleton variant="analytics" />}
      >
        {content}
      </EventToolsAnimatedState>
    );
  }

  return (
    <AppModalShell
      open={visible}
      title={title}
      onClose={onClose}
      closeAriaLabel={closeLabel}
      maxWidthClassName="max-w-[940px]"
      desktopMaxHeight="min(90vh, 760px)"
      zIndexClassName={zIndexClassName}
      leftAction={
        typeof onBack === "function" ? (
          <EventToolsBackButton onClick={onBack} label={backLabel} />
        ) : null
      }
    >
      <EventToolsAnimatedState
        loading={loading}
        skeleton={<EventToolsSkeleton variant="analytics" />}
      >
        {content}
      </EventToolsAnimatedState>
    </AppModalShell>
  );
}
